import type { D3DragEvent } from 'd3-drag';
import { d3 } from './d3';

const CIRCLE_PADDING = 10;

interface Emoji {
  getURL: (shortname: string) => string;
}

interface Bounds {
  x: number;
  y: number;
  radius: number;
}

interface Node {
  color: string;
  emojiShortname: string;
  identifier: string;
  index: number;
  radius: number;
  vx?: number;
  vy?: number;
  x: number;
  y: number;
}

interface T {
  nodes: Node[];
  selector: string;
  emoji: Emoji;
  layout: { width: number; height: number };
}

/**
 * Bubbles: visualization of player bubbles for quiz lobby
 */
export class Bubbles implements T {
  readonly selector: string;
  readonly emoji: Emoji;
  readonly layout: { width: number; height: number };
  nodes: Node[];
  force: d3.Simulation<Node, undefined> = d3.forceSimulation();

  /**
   * @param selector: string selection to a dom node, e.g., #bubbles-${questionId}
   * @param emoji: emoji class with url getter function
   * @param layout: width and height of container
   */
  constructor(props: T) {
    this.nodes = props.nodes;
    this.selector = props.selector;
    this.emoji = props.emoji;
    this.layout = props.layout;
  }

  /**
   * Calculates bounds for path element
   *
   * @returns bounds setter function
   */
  private bounds = (xMax: number, yMax: number): ((d: Bounds) => void) => {
    return (d: Bounds) => {
      if (d.x <= 0) {
        d.x = d.radius;
      } else if (d.x >= xMax - d.radius) {
        d.x = xMax - d.radius;
      }
      if (d.y <= 0) {
        d.y = 0;
      } else if (d.y >= yMax - d.radius) {
        d.y = yMax - d.radius;
      }
    };
  };

  /**
   * Sets forces for collisions and along x and y axis towards center
   */
  public updateForce = (): void => {
    this.force
      .force(
        'collide',
        d3.forceCollide((d: Node) => d.radius + CIRCLE_PADDING).strength(0.7),
      )
      .force('x', d3.forceX(this.layout.width / 2).strength(0.05))
      .force('y', d3.forceY(this.layout.height / 2).strength(0.05));
  };

  /**
   * Setter for nodes list to be used by the force simulation
   *
   * @param nodes: array of player nodes
   */
  public setNodes = (nodes: Node[]): void => {
    this.nodes = nodes;
  };

  /**
   * Visualization updater
   */
  public update = (): void => {
    const { width: xMax, height: yMax } = this.layout;
    // Don't add, remove, or move any nodes during "get ready" state
    const dragmove = (
      event: D3DragEvent<SVGGElement, Node, unknown>,
      d: Node,
    ) => {
      (d as any).px += event.dx;
      (d as any).py += event.dy;
      d.x += event.dx;
      d.y += event.dy;
      tick();
    };
    const dragend = () => {
      this.force.restart().alpha(1); // "Re-heat" the simulation to prevent charge decay
    };

    const drag = d3
      .drag<SVGGElement, Node>()
      .on('drag', dragmove)
      .on('end', dragend);

    // Select
    const node = d3
      .select(this.selector)
      .selectAll<SVGGElement, Node>('.node')
      .data(this.nodes, (d: Node) => d.index);
    // Enter
    const nodeEnter = node
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('opacity', 0)
      .call(drag);
    const nodeCircle = nodeEnter
      .append('circle')
      .attr('r', (d) => d.radius)
      .attr('fill', (d) => d.color);
    const nodeEmoji = nodeEnter.append('svg').attr('class', 'emoji-svg');
    const nodeEmojiUse = nodeEmoji
      .append('use')
      .attr('class', 'emoji')
      .attr('xlink:href', (d) => this.emoji.getURL(d.emojiShortname));
    // Update
    node.merge(nodeEnter).transition().attr('opacity', 1);
    node
      .select<SVGCircleElement>('circle')
      .merge(nodeCircle)
      .transition()
      .attr('r', (d) => d.radius);
    node
      .select<SVGSVGElement>('.emoji-svg')
      .merge(nodeEmoji)
      .transition()
      .attr('height', (d) => d.radius * 2)
      .attr('width', (d) => d.radius * 2)
      .attr('x', (d) => -d.radius)
      .attr('y', (d) => -d.radius);
    node
      .select<SVGUseElement>('.emoji')
      .merge(nodeEmojiUse)
      .transition()
      .attr('width', '100%')
      .attr('height', '100%');
    // Exit
    node.exit().remove();

    const tick = () =>
      node
        .merge(nodeEnter)
        .each(this.bounds(xMax, yMax))
        .attr('transform', (d) => `translate(${d.x},${d.y})`);

    this.force
      .restart()
      .alphaTarget(0.25) // "Re-heat" the simulation to prevent charge decay
      .nodes(this.nodes) // Set node data
      .on('tick', tick);
  };
}
